import { useContext, useEffect, useState, useRef } from "react"
import { PeFilterContext } from "../../Contexts/PeFilterContext"
import FilterWrapper from "./FilterWrapper"
import Constants from "../../Constants"
import useScrollIntoView from "./useScrollIntoView"
import axios from "axios"
import { refineSearchSubFirmTypeUrl } from "../../Config/config"
import { UserContext } from "../../Contexts/UserContext"

function FirmTypeFilter({
  open,
  accordionClick,
  setFirmTypeOptions,
  setFirmSubTypeOptions,
  FirmTypeOptions,
  customPathname,
  dataPath,
}) {
  const { firmTypeResponseData, refineSearchFilters, setSubFirmType } = useContext(PeFilterContext)
  const { getToken } = useContext(UserContext)
  const listItemRef = useScrollIntoView(open)

  const [peSubTypes, setPeSubTypes] = useState([])
  const [vcSubTypes, setVcSubTypes] = useState([])

  const [combinedSubTypes, setCombinedSubTypes] = useState([])

  const [expandedFirmType, setExpandedFirmType] = useState(null)

  const peScrollRef = useRef(null)
  const vcScrollRef = useRef(null)

  const [peScrollPosition, setPeScrollPosition] = useState(0)
  const [vcScrollPosition, setVcScrollPosition] = useState(0)

  const [loadingSubTypes, setLoadingSubTypes] = useState(false)

  useEffect(() => {
    const industryIds = refineSearchFilters[customPathname]?.firmType?.map((value) => value.id) ?? []
    setFirmTypeOptions(
      firmTypeResponseData[dataPath]?.map((industry) => ({
        ...industry,
        isChecked: industryIds.includes(industry.id),
      })),
    )
  }, [customPathname, dataPath, firmTypeResponseData, refineSearchFilters, setFirmTypeOptions])

  useEffect(() => {
    if (peScrollRef.current && expandedFirmType === 1) {
      peScrollRef.current.scrollTop = peScrollPosition
    }
    if (vcScrollRef.current && expandedFirmType === 2) {
      vcScrollRef.current.scrollTop = vcScrollPosition
    }
  }, [peSubTypes, vcSubTypes, expandedFirmType, peScrollPosition, vcScrollPosition])

  useEffect(() => {
    const fetchAllSubTypes = async () => {
      setLoadingSubTypes(true)
      try {
        const response = await axios.post(
          refineSearchSubFirmTypeUrl,
          { firm_type: "All" },
          { headers: { Authorization: getToken() } }
        )

        if (response.status === 200) {
          const selectedSubTypeIds =
            refineSearchFilters[customPathname]?.firm_Sub_type?.map((value) => value.id) ?? []
          const allSubTypes = response.data?.firmType ?? []

          const hasPE = FirmTypeOptions?.some((type) => type.id === 1)
          const hasVC = FirmTypeOptions?.some((type) => type.id === 2)

          if (!hasPE && !hasVC) {
            setPeSubTypes([])
            setVcSubTypes([])
            setCombinedSubTypes([])
            setFirmSubTypeOptions([])
            setLoadingSubTypes(false)
            return
          }

          const peSubTypesArray = []
          const vcSubTypesArray = []

          allSubTypes.forEach((subType) => {
            const displayName = subType.name.replace(/\s*\((PE|VC)\)/g, "").trim()

            const cleanedSubType = {
              ...subType,
              displayName,
              originalName: subType.name,
              isChecked: selectedSubTypeIds.length === 0 || selectedSubTypeIds.includes(subType.id),
            }

            if (subType.name.includes("(PE)")) {
              peSubTypesArray.push(cleanedSubType)
            } else if (subType.name.includes("(VC)")) {
              vcSubTypesArray.push(cleanedSubType)
            } else {
              peSubTypesArray.push(cleanedSubType)
            }
          })

          setPeSubTypes(peSubTypesArray)
          setVcSubTypes(vcSubTypesArray)

          const combined = [
            ...peSubTypesArray.map((type) => ({ ...type, firmType: "PE" })),
            ...vcSubTypesArray.map((type) => ({ ...type, firmType: "VC" })),
          ]
          setCombinedSubTypes(combined)
          setSubFirmType(allSubTypes)

          setFirmSubTypeOptions(combined)
        }
      } catch (error) {
        console.error("Error fetching sub-types:", error)
      }
      setLoadingSubTypes(false)
    }

    fetchAllSubTypes()
  }, [
    customPathname,
    getToken,
    refineSearchFilters,
    setFirmSubTypeOptions,
    setFirmTypeOptions,
    setSubFirmType,
    FirmTypeOptions,
  ])

  useEffect(() => {
    const selectedFirmTypes = FirmTypeOptions?.filter((industry) => industry.isChecked) || []
    const hasPE = selectedFirmTypes.some((type) => type.id === 1)
    const hasVC = selectedFirmTypes.some((type) => type.id === 2)
  }, [FirmTypeOptions])

  const toggleExpand = (firmTypeId, e) => {
    e.stopPropagation()
    setExpandedFirmType(expandedFirmType === firmTypeId ? null : firmTypeId)
  }

  const handleFirmTypeChange = (e) => {
    const { name, checked, id } = e.target

    if (name === "allSelect") {
      const currentFirmTypes = FirmTypeOptions.map((type) => ({ ...type, isChecked: checked }))
      setFirmTypeOptions(currentFirmTypes)
      setExpandedFirmType(null)
    } else {
      const currentFirmTypes = FirmTypeOptions.map((type) => {
        const isNumeric = /^[0-9]+$/.test(id)
        const parsedId = isNumeric ? Number.parseInt(id) : id
        return type.id === parsedId ? { ...type, isChecked: checked } : type
      })

      setFirmTypeOptions(currentFirmTypes)

      const numericId = Number.parseInt(id)

      if (!checked && expandedFirmType === numericId) {
        setExpandedFirmType(null)
      }

      if (checked && expandedFirmType !== numericId) {
        setExpandedFirmType(null)
      }

      if (!checked) {
        if (numericId === 1) {
          setPeSubTypes([])
        } else if (numericId === 2) {
          setVcSubTypes([])
        }
      }
    }
  }

  const handleSubTypeChange = (firmTypeId) => (e) => {
    e.stopPropagation()

    if (firmTypeId === 1 && peScrollRef.current) {
      setPeScrollPosition(peScrollRef.current.scrollTop)
    } else if (firmTypeId === 2 && vcScrollRef.current) {
      setVcScrollPosition(vcScrollRef.current.scrollTop)
    }

    const { name, checked, id } = e.target

    const currentSubTypes = firmTypeId === 1 ? peSubTypes : vcSubTypes

    let updatedList = []

    if (name === "allSelect") {
      updatedList = currentSubTypes.map((item) => ({
        ...item,
        isChecked: checked,
      }))
    } else if (name === "filteredSelect" && e.target.filteredIds) {
      updatedList = currentSubTypes.map((item) =>
        e.target.filteredIds.includes(item.id) ? { ...item, isChecked: checked } : item,
      )
    } else {
      const actualId = e.target.dataset.subtypeId ? Number.parseInt(e.target.dataset.subtypeId) : Number.parseInt(id)
      updatedList = currentSubTypes.map((item) => (item.id === actualId ? { ...item, isChecked: checked } : item))
    }

    if (firmTypeId === 1) {
      setPeSubTypes(updatedList)
    } else {
      setVcSubTypes(updatedList)
    }

    const otherSubTypes = firmTypeId === 1 ? vcSubTypes : peSubTypes
    const combined = [
      ...(firmTypeId === 1 ? updatedList : otherSubTypes).map((type) => ({ ...type, firmType: "PE" })),
      ...(firmTypeId === 2 ? updatedList : otherSubTypes).map((type) => ({ ...type, firmType: "VC" })),
    ]

    setCombinedSubTypes(combined)

    setFirmSubTypeOptions(combined)
  }

  const handleSelectAll = (firmTypeId) => (e) => {
    e.stopPropagation()

    const checked = e.target.checked

    const syntheticEvent = {
      target: {
        name: "allSelect",
        checked: checked,
        stopPropagation: () => {},
      },
    }

    handleSubTypeChange(firmTypeId)(syntheticEvent)
  }

  const firmTypeSelectedCount = FirmTypeOptions?.filter((option) => option.isChecked)?.length ?? 0
  const peSubTypeSelectedCount = peSubTypes?.filter((option) => option.isChecked)?.length ?? 0
  const vcSubTypeSelectedCount = vcSubTypes?.filter((option) => option.isChecked)?.length ?? 0
  const totalSubTypesSelectedCount = peSubTypeSelectedCount + vcSubTypeSelectedCount

  const StyledMultiSelectEntity = ({ handleEntityChange, primaryEntity, firmTypeId }) => {
    const scrollRef = firmTypeId === 1 ? peScrollRef : vcScrollRef

    return (
      <div className="multiselect-container">
        <div className="entity-list" style={{ maxHeight: "8rem", overflowY: "auto" }} ref={scrollRef}>
          {loadingSubTypes ? (
            <></>
          ) : primaryEntity && primaryEntity.length > 0 ? (
            <>
              {primaryEntity.map((entity, index) => (
                <div
                  className="form-check font-sans_medium_body cursor-default flex text-left py-2 pr-2 w-full font-medium text-gray-700 border-b border-gray-100 hover:bg-gray-50"
                  key={entity.id || index}
                >
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name={entity.name.replace(/\s*\((PE|VC)\)/g, "").trim()}
                    id={`subtype-${firmTypeId}-${entity.id}`}
                    data-subtype-id={entity.id}
                    checked={entity?.isChecked || false}
                    onChange={handleEntityChange}
                  />
                  <label
                    htmlFor={`subtype-${firmTypeId}-${entity.id}`}
                    className="form-check-label mx-2 mr-2 cursor-pointer text-[13px] text-[#333333] leading-[17px] font-sans_book_body"
                  >
                    {entity.displayName}
                  </label>
                </div>
              ))}
            </>
          ) : (
            <></>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="border-b-[3px] border-[#E8E6E1]">
      <li className={open ? "bg-white relative px-3 py-1" : "relative px-3 py-1"} ref={listItemRef}>
        <FilterWrapper
          count={firmTypeSelectedCount}
          isSelect={false}
          label="Firm Type"
          onClick={accordionClick}
          open={open}
          definitionInfo={Constants.firmType}
        />
        <div
          className="relative overflow-hidden transition-all max-h-0 duration-700 overflow-y-auto no-scrollbar"
          style={{ maxHeight: open ? "30rem" : "" }}
        >
          {open === true && (
            <div className="flex flex-col">
              {FirmTypeOptions?.map((firmType) => (
                <div key={firmType.id} className="mb-2">
                  <div className="flex items-center">
                    <div className="form-check font-sans_medium_body cursor-default flex items-center text-left py-2 pr-2 w-full font-medium text-gray-700 border-b border-gray-100 hover:bg-gray-50">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        name={firmType.name}
                        id={firmType.id}
                        checked={firmType?.isChecked || false}
                        onChange={handleFirmTypeChange}
                      />

                      <label
                        htmlFor={firmType.id}
                        className="form-check-label mx-2 mr-2 cursor-pointer text-[13px] text-[#333333] leading-[17px] font-sans_book_body flex items-center"
                      >
                        {firmType.name.replace(/$$[^)]*$$/g, "").trim()}

                        {(firmType.name === 'PE'|| firmType.name === 'VC') &&
                          firmType.isChecked &&
                          (
                            <button
                              type="button"
                              onClick={(e) => toggleExpand(firmType.id, e)}
                              className="ml-3 text-[#BC8B20] text-xs flex items-center"
                            >
                              {expandedFirmType === firmType.id ? "▼" : "▶"}
                              {firmType.id === 1 && peSubTypeSelectedCount > 0 && (
                                <span className="ml-1">({peSubTypeSelectedCount})</span>
                              )}
                              {firmType.id === 2 && vcSubTypeSelectedCount > 0 && (
                                <span className="ml-1">({vcSubTypeSelectedCount})</span>
                              )}
                            </button>
                          )}
                      </label>
                    </div>
                  </div>

                  {firmType.isChecked && expandedFirmType === firmType.id && (
                    <div className="ml-6 border-l-2 pl-2 border-[#E8E6E1] mt-2">
                      {firmType.id === 1 && (
                        <StyledMultiSelectEntity
                          handleEntityChange={handleSubTypeChange(1)}
                          primaryEntity={peSubTypes}
                          firmTypeId={1}
                        />
                      )}
                      {firmType.id === 2 && (
                        <StyledMultiSelectEntity
                          handleEntityChange={handleSubTypeChange(2)}
                          primaryEntity={vcSubTypes}
                          firmTypeId={2}
                        />
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </li>
    </div>
  )
}

export default FirmTypeFilter